import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Plus, X, Send, ExternalLink, Trash2 } from 'lucide-react';

const STATUS_STYLE = {
  draft:    'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-300',
  sent:     'bg-blue-100 text-blue-700',
  viewed:   'bg-amber-100 text-amber-700',
  accepted: 'bg-green-100 text-green-700',
  declined: 'bg-red-100 text-red-700',
  expired:  'bg-gray-200 text-gray-500',
};

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

async function apiPatch(path, body) {
  const res = await fetch(`/api${path}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${localStorage.getItem('mickey_token')}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `Request failed (${res.status})`);
  return json;
}

export default function ProposalsPage() {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // proposal | 'new'

  const load = useCallback(async () => {
    try { setProposals((await api.get('/proposals')).proposals || []); setError(''); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-8 text-[var(--text-secondary)]">Loading proposals…</div>;

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[var(--text-primary)]">Proposals</h1>
          <p className="text-sm text-[var(--text-secondary)]">
            {proposals.length} total · {proposals.filter((p) => p.status === 'accepted').length} accepted ·{' '}
            {money(proposals.filter((p) => p.status === 'accepted').reduce((s, p) => s + Number(p.total || 0), 0))} won
          </p>
        </div>
        <button onClick={() => setEditing('new')}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90">
          <Plus size={16} /> New Proposal
        </button>
      </div>
      {error && <div className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <div className="overflow-hidden rounded-xl border border-[var(--border)]">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-[var(--text-secondary)] dark:bg-slate-800">
            <tr>
              <th className="px-4 py-2.5">#</th>
              <th className="px-4 py-2.5">Title</th>
              <th className="px-4 py-2.5">Account</th>
              <th className="px-4 py-2.5 text-right">Total</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">Activity</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {proposals.map((p) => (
              <tr key={p.id} onClick={() => setEditing(p)}
                className="cursor-pointer border-t border-[var(--border)] bg-white hover:bg-gray-50 dark:bg-slate-900 dark:hover:bg-slate-800">
                <td className="px-4 py-3 font-mono text-xs text-[var(--text-secondary)]">{p.number}</td>
                <td className="px-4 py-3 font-medium text-[var(--text-primary)]">{p.title}</td>
                <td className="px-4 py-3 text-[var(--text-secondary)]">{p.account_name || '—'}</td>
                <td className="px-4 py-3 text-right font-medium">{money(p.total)}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLE[p.status] || ''}`}>{p.status}</span></td>
                <td className="px-4 py-3 text-xs text-[var(--text-secondary)]">
                  {p.accepted_at ? `accepted ${new Date(p.accepted_at).toLocaleDateString()}`
                    : p.first_viewed_at ? `viewed ${new Date(p.first_viewed_at).toLocaleDateString()}`
                    : p.sent_at ? `sent ${new Date(p.sent_at).toLocaleDateString()}` : '—'}
                </td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <a href={`/p/${p.public_token}?preview=1`} target="_blank" rel="noreferrer"
                    title="Preview customer view" className="text-[var(--text-secondary)] hover:text-primary"><ExternalLink size={15} /></a>
                </td>
              </tr>
            ))}
            {!proposals.length && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-[var(--text-secondary)]">No proposals yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <ProposalEditor proposal={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function ProposalEditor({ proposal, onClose, onSaved }) {
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState({
    title: proposal?.title || '',
    account_id: proposal?.account_id || '',
    contact_id: proposal?.contact_id || '',
    recipient_email: proposal?.recipient_email || '',
    valid_until: proposal?.valid_until || '',
    scope_notes: proposal?.scope_notes || '',
    terms: proposal?.terms || 'Net 30. Material lead time confirmed at order. Freight quoted separately unless itemized above.',
    tax: proposal?.tax || 0,
  });
  const [items, setItems] = useState(proposal?.line_items?.length ? proposal.line_items : [{ description: '', qty: 1, unit: '', unit_price: '' }]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');

  useEffect(() => {
    api.get('/crm/accounts').then((r) => setAccounts(r.accounts || [])).catch(() => {});
    api.get('/crm/contacts').then((r) => setContacts(r.contacts || [])).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setItem = (i, k) => (e) => setItems((arr) => arr.map((it, j) => (j === i ? { ...it, [k]: e.target.value } : it)));
  const subtotal = items.reduce((s, li) => s + (Number(li.qty) || 0) * (Number(li.unit_price) || 0), 0);
  const accountContacts = contacts.filter((c) => !form.account_id || c.account_id === form.account_id);
  const input = 'mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-3 py-2 text-sm dark:bg-slate-800';

  async function save(thenSend) {
    if (!form.title.trim()) { setErr('Title required'); return null; }
    setBusy(thenSend ? 'sending' : 'saving'); setErr('');
    try {
      const body = {
        ...form,
        account_id: form.account_id || null, contact_id: form.contact_id || null,
        valid_until: form.valid_until || null, recipient_email: form.recipient_email || null,
        tax: Number(form.tax) || 0,
        line_items: items.filter((li) => li.description.trim()).map((li) => ({
          description: li.description, qty: Number(li.qty) || 0, unit: li.unit || '', unit_price: Number(li.unit_price) || 0,
        })),
      };
      const saved = proposal
        ? (await apiPatch(`/proposals/${proposal.id}`, body)).proposal
        : (await api.post('/proposals', body)).proposal;
      if (thenSend) {
        const to = form.recipient_email || contacts.find((c) => c.id === form.contact_id)?.email;
        if (!to) { setErr('Set a recipient email (or pick a contact with one) before sending.'); setBusy(''); return saved; }
        await api.post(`/proposals/${saved.id}/send`, { to });
      }
      onSaved();
      return saved;
    } catch (e) { setErr(e.message); setBusy(''); return null; }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/30 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="my-8 w-full max-w-2xl space-y-4 rounded-xl bg-white p-6 shadow-xl dark:bg-slate-900">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">
            {proposal ? `Edit ${proposal.number}` : 'New Proposal'}
            {proposal && <span className={`ml-3 rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLE[proposal.status]}`}>{proposal.status}</span>}
          </h2>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800"><X size={18} /></button>
        </div>
        {err && <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}

        <label className="block text-xs font-medium text-[var(--text-secondary)]">Title *<input className={input} value={form.title} onChange={set('title')} placeholder="Optimal Slab — Building 4 warehouse floor" /></label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs font-medium text-[var(--text-secondary)]">Account
            <select className={input} value={form.account_id || ''} onChange={(e) => setForm((f) => ({ ...f, account_id: e.target.value, contact_id: '' }))}>
              <option value="">—</option>{accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </label>
          <label className="block text-xs font-medium text-[var(--text-secondary)]">Contact
            <select className={input} value={form.contact_id || ''} onChange={set('contact_id')}>
              <option value="">—</option>{accountContacts.map((c) => <option key={c.id} value={c.id}>{c.name}{c.email ? ` (${c.email})` : ''}</option>)}
            </select>
          </label>
          <label className="block text-xs font-medium text-[var(--text-secondary)]">Send to (email)<input type="email" className={input} value={form.recipient_email} onChange={set('recipient_email')} placeholder="defaults to contact email" /></label>
          <label className="block text-xs font-medium text-[var(--text-secondary)]">Valid until<input type="date" className={input} value={form.valid_until || ''} onChange={set('valid_until')} /></label>
        </div>

        <div>
          <p className="mb-1 text-xs font-medium text-[var(--text-secondary)]">Line items</p>
          <div className="space-y-2">
            {items.map((li, i) => (
              <div key={i} className="flex items-center gap-2">
                <input className={`${input} mt-0 flex-1`} placeholder="Description" value={li.description} onChange={setItem(i, 'description')} />
                <input className={`${input} mt-0 w-20`} placeholder="Qty" type="number" value={li.qty} onChange={setItem(i, 'qty')} />
                <input className={`${input} mt-0 w-20`} placeholder="Unit" value={li.unit} onChange={setItem(i, 'unit')} />
                <input className={`${input} mt-0 w-28`} placeholder="$ / unit" type="number" step="0.01" value={li.unit_price} onChange={setItem(i, 'unit_price')} />
                <button type="button" onClick={() => setItems((arr) => arr.filter((_, j) => j !== i))}
                  className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
          <div className="mt-2 flex items-center justify-between">
            <button type="button" onClick={() => setItems((arr) => [...arr, { description: '', qty: 1, unit: '', unit_price: '' }])}
              className="text-sm text-primary hover:underline">+ Add line</button>
            <p className="text-sm text-[var(--text-secondary)]">
              Subtotal <b className="text-[var(--text-primary)]">{money(subtotal)}</b>
              <span className="mx-2">·</span>Tax <input type="number" step="0.01" value={form.tax} onChange={set('tax')}
                className="w-24 rounded border border-[var(--border)] bg-white px-2 py-1 text-right text-sm dark:bg-slate-800" />
              <span className="mx-2">·</span>Total <b className="text-[var(--text-primary)]">{money(subtotal + (Number(form.tax) || 0))}</b>
            </p>
          </div>
        </div>

        <label className="block text-xs font-medium text-[var(--text-secondary)]">Scope notes (shown to customer)
          <textarea rows={3} className={input} value={form.scope_notes} onChange={set('scope_notes')} /></label>
        <label className="block text-xs font-medium text-[var(--text-secondary)]">Terms
          <textarea rows={2} className={input} value={form.terms} onChange={set('terms')} /></label>

        <div className="flex items-center justify-between pt-2">
          <div className="text-xs text-[var(--text-secondary)]">
            {proposal && <a className="text-primary hover:underline" href={`/p/${proposal.public_token}?preview=1`} target="_blank" rel="noreferrer">Preview customer view ↗</a>}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-[var(--border)] px-4 py-2 text-sm">Cancel</button>
            <button type="button" disabled={!!busy} onClick={() => save(false)}
              className="rounded-lg border border-primary px-4 py-2 text-sm font-medium text-primary disabled:opacity-50">
              {busy === 'saving' ? 'Saving…' : 'Save Draft'}
            </button>
            <button type="button" disabled={!!busy} onClick={() => save(true)}
              className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
              <Send size={14} /> {busy === 'sending' ? 'Sending…' : 'Save & Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
